/**
 * Energy-saving poll pacing. Background loops use [throttledDelay] so they slow down on battery, on a
 * cellular network or while the tab is hidden — fewer wakeups and requests when nobody is looking.
 */

let batteryCharging = true
let cellularNetwork = false

// Throttle when on battery, tab hidden, or on cellular.
function isThrottled(): boolean {
  return !batteryCharging || document.hidden || cellularNetwork
}

// Battery Status / Network Information APIs aren't in the standard DOM lib types (and exist only in
// Chromium); narrow the shapes we use instead of reaching for `any`.
interface BatteryManager {
  charging: boolean
  addEventListener(type: "chargingchange", listener: () => void): void
}
interface NetworkInformation {
  type?: string
  addEventListener(type: "change", listener: () => void): void
}

export function initEnergySave(): void {
  const nav = navigator as Navigator & {
    getBattery?: () => Promise<BatteryManager>
    connection?: NetworkInformation
  }

  // Battery Status API (Chrome/Edge/Opera, not Firefox/Safari)
  if (typeof nav.getBattery === "function") {
    void nav.getBattery().then((battery) => {
      batteryCharging = battery.charging
      battery.addEventListener("chargingchange", () => {
        batteryCharging = battery.charging
      })
    })
  }

  // Network Information API (Chrome/Edge, not Firefox/Safari)
  const connection = nav.connection
  if (connection) {
    cellularNetwork = connection.type === "cellular"
    connection.addEventListener("change", () => {
      cellularNetwork = connection.type === "cellular"
    })
  }
}

/**
 * Sleep for [normalMs], or [throttledMs] while throttled. Sleeps in chunks and re-reads the state each
 * time, so plugging in shortens a wait already in progress (and unplugging lengthens it) within 30 s.
 *
 * The target is compared against the time ALREADY waited rather than counted down, and that is what
 * makes both directions work. A countdown that leaves the loop as soon as `!isThrottled()` returns after
 * the very first chunk — during a normal wait that condition is true by definition — so every caller
 * would silently poll at `min(normalMs, 30_000)` while looking like it asked for more.
 */
export async function throttledDelay(normalMs: number, throttledMs: number): Promise<void> {
  const chunkMs = 30_000
  let waited = 0

  for (;;) {
    const target = isThrottled() ? throttledMs : normalMs
    if (waited >= target) return

    const chunk = Math.min(target - waited, chunkMs)
    await new Promise((resolve) => setTimeout(resolve, chunk))
    waited += chunk
  }
}
